import { useEffect, useState } from 'react'
import { BRANDS, BRAND_INFO, CHANNELS, CONTENT_TYPES, STATUSES, publishingHints } from '@hub/shared'
import type { Channel } from '@hub/shared'
import { iso, long, relative } from '../lib/dates.js'
import { useArchiveItem, useItem, useUpdateItem } from '../lib/items.js'
import type { ItemDetail, ItemPatch } from '../lib/items.js'
import { STATUS_TONE, brandFill, channelLabel, statusLabel, typeLabel } from './Brand.js'
import { Icon } from './Icon.js'
import { ItemFiles } from './ItemFiles.js'
import { ItemLinks } from './ItemLinks.js'
import { Sheet } from './Sheet.js'

const ACTION_LABEL: Record<string, string> = {
  angelegt: 'angelegt',
  status: 'Phase geändert',
  geändert: 'bearbeitet',
  verknüpft: 'verknüpft',
  archiviert: 'archiviert',
  wiederhergestellt: 'wiederhergestellt',
}

/** Text that saves when focus leaves it — no save button to forget. */
function useDraft(value: string, save: (v: string) => void): [string, (v: string) => void, () => void] {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  return [draft, setDraft, () => draft !== value && save(draft)]
}

function Body({ item, onClose }: { item: ItemDetail; onClose: () => void }): JSX.Element {
  const update = useUpdateItem()
  const archive = useArchiveItem()
  const save = (patch: ItemPatch): void => update.mutate({ id: item.id, patch })

  const [title, setTitle, commitTitle] = useDraft(item.title, (v) => v.trim() && save({ title: v.trim() }))
  const [note, setNote, commitNote] = useDraft(item.note, (v) => save({ note: v }))
  const [path, setPath, commitPath] = useDraft(item.path, (v) => save({ path: v.trim() }))
  const [showAll, setShowAll] = useState(false)
  const color = brandFill(item.brand)
  const hints = publishingHints(item.channels)

  const toggleChannel = (c: Channel): void =>
    save({ channels: item.channels.includes(c) ? item.channels.filter((x) => x !== c) : [...item.channels, c] })

  return (
    <>
      <div className="px-5 pb-3 pt-3" data-sheet-drag>
        <div className="flex items-start gap-3">
          <span className="mt-2 h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: color }} />
          <textarea
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={commitTitle}
            rows={1}
            className="min-h-0 flex-1 resize-none bg-transparent text-h3 leading-tight text-label outline-none [field-sizing:content]"
          />
          <button type="button" onClick={onClose} className="glass-circle h-9 w-9 shrink-0" aria-label="Schließen">
            <Icon name="close" size={16} />
          </button>
        </div>
        <p className="mt-1 pl-6 text-small text-label2/60">
          {long(item.start)} · {typeLabel(item.type)}
          {item.archived && ' · archiviert'}
        </p>
      </div>

      <div className="min-h-0 min-w-0 flex-1 space-y-6 overflow-y-auto overflow-x-hidden overscroll-contain px-5 pb-6">
        {/* Status as a stepper along the pipeline */}
        <div className="flex flex-wrap gap-1.5">
          {STATUSES.map((s) => {
            const on = item.status === s
            return (
              <button
                key={s}
                type="button"
                onClick={() => save({ status: s })}
                aria-pressed={on}
                className={`flex h-8 items-center gap-1.5 rounded-full px-3 text-small transition-colors ${
                  on ? 'bg-label font-semibold text-cell' : 'bg-fill/[0.1] text-label2/80 hover:bg-fill/[0.16]'
                }`}
              >
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: STATUS_TONE[s] }} />
                {statusLabel(s)}
              </button>
            )
          })}
        </div>

        {hints.length > 0 && (
          <section className="rounded-[14px] bg-fill/[0.06] px-4 py-3">
            <p className="text-caption font-semibold uppercase tracking-wide text-label2/50">Beim Veröffentlichen</p>
            <ul className="mt-1.5 space-y-1">
              {hints.map((h) => (
                <li key={h} className="text-small text-label2/80">
                  {h}
                </li>
              ))}
            </ul>
          </section>
        )}

        <ItemFiles itemId={item.id} hasPath={item.path !== ''} />

        <ItemLinks item={item} />

        {/* Details */}
        <section className="space-y-3">
          <p className="section-title px-0">Details</p>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-caption text-label2/60">Ausspieltag</span>
              <input type="date" className="field mt-1 min-h-[42px] text-small" value={item.start} onChange={(e) => e.target.value && save({ start: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-caption text-label2/60">Ereignis (optional)</span>
              <input type="date" className="field mt-1 min-h-[42px] text-small" value={item.eventDate ?? ''} onChange={(e) => save({ eventDate: e.target.value || null })} />
            </label>
            <label className="block">
              <span className="text-caption text-label2/60">Marke</span>
              <select className="field mt-1 min-h-[42px] text-small" value={item.brand} onChange={(e) => save({ brand: e.target.value as never })}>
                {BRANDS.map((b) => (
                  <option key={b} value={b}>
                    {BRAND_INFO[b].name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-caption text-label2/60">Art</span>
              <select className="field mt-1 min-h-[42px] text-small" value={item.type} onChange={(e) => save({ type: e.target.value as never })}>
                {CONTENT_TYPES.map((c) => (
                  <option key={c} value={c}>
                    {typeLabel(c)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {CHANNELS.map((c) => {
              const on = item.channels.includes(c)
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleChannel(c)}
                  className={`h-7 rounded-full px-2.5 text-caption transition-colors ${on ? 'bg-label font-medium text-cell' : 'bg-fill/[0.1] text-label2/70'}`}
                >
                  {channelLabel(c)}
                </button>
              )
            })}
          </div>
          <textarea
            className="field min-h-[96px] py-3 text-small"
            placeholder="Notiz"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            onBlur={commitNote}
          />
          <label className="flex items-center gap-2 rounded-[14px] bg-fill/[0.08] px-3">
            <Icon name="folder" size={16} className="text-label2/50" />
            <input
              className="h-10 flex-1 bg-transparent text-caption outline-none placeholder:text-label2/40"
              placeholder={`Ordner, z. B. ${item.brand === 'edelband' ? 'campaigns' : 'Campaigns'}/Herbst-2026`}
              value={path}
              onChange={(e) => setPath(e.target.value)}
              onBlur={commitPath}
            />
          </label>
        </section>

        {/* History */}
        {item.activity.length > 0 && (
          <section>
            <p className="section-title px-0">Verlauf</p>
            <ol className="space-y-2 border-l border-sep/20 pl-4">
              {(showAll ? item.activity : item.activity.slice(0, 5)).map((a, i) => (
                <li key={i} className="text-caption text-label2/70">
                  <span className="font-medium text-label">{a.actor === 'claude' ? 'Claude' : 'Lucas'}</span> {ACTION_LABEL[a.action] ?? a.action}
                  {(() => {
                    const d = a.diff as { status?: { zu?: string }; grund?: string } | null
                    const to = d?.status?.zu
                    return (
                      <>
                        {to && STATUSES.includes(to as never) && <> → {statusLabel(to as never)}</>}
                        {d?.grund && <span className="text-label2/55"> ({d.grund})</span>}
                      </>
                    )
                  })()}
                  <span className="text-label2/45">
                    {' · '}
                    {relative(iso(new Date(a.at)))}, {new Date(a.at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </li>
              ))}
            </ol>
            {item.activity.length > 5 && !showAll && (
              <button type="button" onClick={() => setShowAll(true)} className="mt-1 text-caption text-pebro">
                Alles anzeigen
              </button>
            )}
          </section>
        )}

        <button
          type="button"
          onClick={() => archive.mutate({ id: item.id, archived: !item.archived }, { onSuccess: () => !item.archived && onClose() })}
          className="flex items-center gap-2 text-small text-label2/60 hover:text-danger"
        >
          <Icon name="archive" size={16} />
          {item.archived ? 'Wiederherstellen' : 'Archivieren'}
        </button>
      </div>
    </>
  )
}

export function ItemSheet({ id, onClose }: { id: string; onClose: () => void }): JSX.Element {
  const { data, error } = useItem(id)
  return (
    <Sheet onClose={onClose} label="Eintrag">
      {data ? (
        <Body item={data} onClose={onClose} />
      ) : (
        <div className="grid flex-1 place-items-center p-10 text-small text-label2/60">
          {error ? 'Eintrag nicht gefunden.' : 'Lädt …'}
        </div>
      )}
    </Sheet>
  )
}
